import type { Issue } from "contracts/api/issue/responses";
import type {
  MapActionCategory,
  MapActionEvidenceState,
  MapFutureBriefLevel,
  MapMigrationWaveKey,
} from "../../enums/map";
import { relevantMapIssues } from "./mapEvidence";
import {
  categoryForMapChange,
  evidenceStateForChange,
  futureBriefLevelForChange,
  highestSeverity,
  migrationWave,
  uniqueCanonicalIssues,
} from "./mapPreviewActionRules";
import { consolidateMapPreviewActions } from "./mapPreviewConsolidation";
import type { MapPreviewModel } from "./mapPreviewModel";
import type { MapChange } from "./mapTypes";

export type MapPreviewAction = {
  change: MapChange;
  sourceChangeIds: string[];
  category: MapActionCategory | null;
  futureBriefLevel: MapFutureBriefLevel;
  evidenceState: MapActionEvidenceState;
  evidenceSeverity: Issue["severity"] | null;
  causalIssues: Issue[];
  relatedFlowLabels: string[];
  concernCount: number;
  wave: MapMigrationWaveKey | null;
};

export function buildMapPreviewActions(
  preview: MapPreviewModel,
  changes: MapChange[],
): MapPreviewAction[] {
  return consolidateMapPreviewActions(
    changes.map((change) => actionFromChange(preview, change)),
  );
}

export function sequencedMapActions(
  actions: MapPreviewAction[],
): MapPreviewAction[] {
  return actions.filter((action) => action.wave !== null);
}

function actionFromChange(
  preview: MapPreviewModel,
  change: MapChange,
): MapPreviewAction {
  const causalIssues = uniqueCanonicalIssues(
    relevantMapIssues(change.type, change.pageFacts),
  );
  const category = categoryForMapChange(change);
  const evidenceSeverity = highestSeverity(causalIssues);
  const evidenceState = evidenceStateForChange(change, causalIssues);
  const pageIds = new Set(change.pages.map((page) => page.id));
  const relatedFlows = preview.flowPreview.all.filter((flow) =>
    flow.uniquePages.some((page) => pageIds.has(page.id)),
  );
  return {
    change,
    sourceChangeIds: [change.id],
    category,
    futureBriefLevel: futureBriefLevelForChange(change),
    evidenceState,
    evidenceSeverity,
    causalIssues,
    relatedFlowLabels: [...new Set(relatedFlows.map((flow) => flow.label))],
    concernCount: causalIssues.length,
    wave: migrationWave(change, category, evidenceState, evidenceSeverity),
  };
}
